import type { FC } from 'react'
import { Controller, useWatch } from 'react-hook-form'
import type { UseFormReturn } from 'react-hook-form'
import {
	ColumnLayout,
	Container,
	FormField,
	Header,
	Input,
	SpaceBetween,
	Toggle
} from '@cloudscape-design/components'
import { MONGODB_CLUSTER_TYPE } from '../../../helper/mongodbClusterConfig'
import type { MongoDBClusterFormValues } from '../../../helper/mongodbClusterConfig'

const fields = [
	{ name: 'instance_size', label: 'Instance size', placeholder: 'e.g. M20' },
	{
		name: 'node_count',
		label: 'Node count',
		placeholder: 'Enter an odd number'
	},
	{
		name: 'disk_size_gb',
		label: 'Disk size (GB)',
		placeholder: 'Enter disk size'
	}
] as const

interface ClusterConfigurationProps {
	form: UseFormReturn<MongoDBClusterFormValues>
	disabled?: boolean
}

const ClusterConfiguration: FC<ClusterConfigurationProps> = ({
	form,
	disabled = false
}) => {
	const autoscalingEnabled = useWatch({
		control: form.control,
		name: 'auto_scaling_compute_enabled'
	})

	return (
		<Container
			header={<Header variant="h2">MongoDB Cluster Configuration</Header>}
		>
			<SpaceBetween direction="vertical" size="m">
				<ColumnLayout columns={2} variant="default">
					<Controller
						name="name"
						control={form.control}
						render={({ field, fieldState: { error } }) => (
							<FormField label="Name" errorText={error?.message}>
								<Input
									ref={field.ref}
									name={field.name}
									value={field.value}
									disabled={disabled}
									placeholder="Enter cluster name"
									onBlur={field.onBlur}
									onChange={({ detail }) => {
										field.onChange(detail.value)
										form.clearErrors('name')
									}}
								/>
							</FormField>
						)}
					/>
					<FormField label="Cluster type">
						<Input value={MONGODB_CLUSTER_TYPE} readOnly disabled />
					</FormField>
					{fields.map(({ name, label, placeholder }) => (
						<Controller
							key={name}
							name={name}
							control={form.control}
							render={({ field, fieldState: { error } }) => (
								<FormField label={label} errorText={error?.message}>
									<Input
										ref={field.ref}
										name={field.name}
										value={field.value}
										disabled={disabled}
										type={name === 'instance_size' ? 'text' : 'number'}
										placeholder={placeholder}
										onBlur={field.onBlur}
										onChange={({ detail }) => {
											field.onChange(detail.value)
											form.clearErrors(name)
										}}
									/>
								</FormField>
							)}
						/>
					))}
				</ColumnLayout>
				<Controller
					name="auto_scaling_compute_enabled"
					control={form.control}
					render={({ field }) => (
						<Toggle
							checked={field.value}
							disabled={disabled}
							onChange={({ detail }) => {
								field.onChange(detail.checked)
								if (!detail.checked) {
									form.setValue('auto_scaling_compute_min_instance_size', '')
									form.setValue('auto_scaling_compute_max_instance_size', '')
								}
								form.clearErrors([
									'auto_scaling_compute_min_instance_size',
									'auto_scaling_compute_max_instance_size'
								])
								void form.trigger('instance_size')
							}}
						>
							Enable compute autoscaling
						</Toggle>
					)}
				/>
				{autoscalingEnabled && (
					<ColumnLayout columns={2} variant="default">
						{(['min', 'max'] as const).map((bound) => {
							const name =
								`auto_scaling_compute_${bound}_instance_size` as const
							return (
								<Controller
									key={bound}
									name={name}
									control={form.control}
									render={({ field, fieldState: { error } }) => (
										<FormField
											label={
												bound === 'min'
													? 'Minimum instance size'
													: 'Maximum instance size'
											}
											errorText={error?.message}
										>
											<Input
												ref={field.ref}
												name={field.name}
												value={field.value}
												disabled={disabled}
												placeholder={bound === 'min' ? 'e.g. M10' : 'e.g. M30'}
												onBlur={field.onBlur}
												onChange={({ detail }) => {
													field.onChange(detail.value)
													form.clearErrors(name)
													void form.trigger('instance_size')
												}}
											/>
										</FormField>
									)}
								/>
							)
						})}
					</ColumnLayout>
				)}
			</SpaceBetween>
		</Container>
	)
}

export default ClusterConfiguration
